import {forwardRef, useEffect} from "react";
import {BrowserRouter, useLocation} from "react-router";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import axios from "axios";
import {getStoredSession, removeStoredSession, setStoredSession,} from "./LocalStorageService";
//import { env } from "./env";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import {getApiBaseUrl, getApiUrl, myFunction} from "./global";
import {GlobalStyle, theme} from "./GlobalStyle";
import {ThemeProvider} from "@mui/material/styles";
import './component/chart/scales';
import {LOADING_WASH_ELEMENT_ID, noteRequestSettled, noteRequestStarted} from "./utils/loadingWash";
import {announcesItsFailures} from "./utils/requestNotices";

/*
 * The full-window white wash shown while the reader is waiting on a request.
 *
 * Raised and lowered by the axios interceptors below rather than by any component, so that every call
 * site gets it without asking for it. Which is also the hazard: a request the reader never made would
 * wash the page out just the same, so anything on a timer has to say so — see utils/loadingWash.
 *
 * Starts hidden by the inline `display: none`, which beats the `flex` in `sx`; the interceptors toggle
 * that inline value.
 */
const CircularIndeterminate = () => {
    return (
        <Box
            style={{display: "none"}}
            id={LOADING_WASH_ELEMENT_ID}
            sx={{
                display: "flex",
                zIndex: "99999",
                position: "absolute",
                height: "100vh",
                width: "100vw",
                opacity: "0.5",
                background: "white",
            }}
        >
            <CircularProgress
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                }}
            />
        </Box>
    );
};

// For GET requests
axios.interceptors.request.use(
    (config) => {
        const token = getStoredSession() ? getStoredSession().access : null;
        if (token && config.url.includes(`${process.env.REACT_APP_GRAPH_ENDPOINT}`)) {
            config.headers["x-api-key"] = `${process.env.REACT_APP_X_API_KEY}`;
        } else if (token && !config.url.includes("https://maps.googleapis.com")) {
            config.headers["Authorization"] = "Bearer " + token;
        }
        noteRequestStarted(config);

        // config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// For POST requests
axios.interceptors.response.use(
    (response) => {
        noteRequestSettled(response?.config);
        if (response?.status == 200 && response?.data?.error) {
            myFunction(response?.data);
        }
        return response;
    },
    async function (error) {
        const originalRequest = error.config;
        // if (
        //   error.response.status === 401 &&
        //   originalRequest.url === "http://127.0.0.1:3000/v1/auth/token"
        // ) {
        //   router.push("/login");
        //   return Promise.reject(error);
        // }
        noteRequestSettled(originalRequest);

        /*
         * An error that carries no response is handed straight back, before anything here reads a status
         * off it.
         *
         * Every branch below asked for `error.response.status` unguarded, and a great many errors have no
         * response at all: a cancelled request, a dropped connection, a timeout, a CORS refusal. For all
         * of those this handler threw a TypeError of its own — 'Cannot read properties of undefined
         * (reading status)' — and because a rejected interceptor is what the caller receives, that
         * TypeError *replaced* the real error on its way out. Every caller in the app was then holding a
         * different error than the one that happened.
         *
         * What that cost, as reported: the dashboard's Media Metrics card said COUNTS UNAVAILABLE with
         * thirteen zeros on first load and correct figures the moment Refresh was pressed. The card aborts
         * its first batch when its scope arrives (a job id, a cid, the header's job list), which is
         * ordinary and is meant to be silent — every consumer detects it by `error.name === 'CanceledError'`.
         * Rewritten as a TypeError, the cancellation was undetectable: the retriever counted it as a
         * failure and blanked the card, and fetchTapeStatusCount's retry re-sent all thirteen counts that
         * had just been deliberately cancelled. Twenty-six concurrent COUNTs over vwItemsAPI is also what
         * then produced the genuine 502 from nginx in the same console.
         *
         * So this is the load-bearing line for cancellation working anywhere in the portal. Note it is
         * placed after noteRequestSettled, which must still run or the loading wash never lifts.
         */
        const status = error.response?.status;
        if (status === undefined) return Promise.reject(error);

        // Only now worth resolving: getApiUrl reads the runtime config, and there is nothing to refresh
        // against for an error that never reached the API.
        let url = await getApiUrl();
        const apiUrl =
            url + getApiBaseUrl() + "/auth/token/refresh/";
        if (
            !originalRequest?.url?.includes("token/refresh/") &&
            status === 401 &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;
            // console.log();
            const refreshToken = getStoredSession() ? getStoredSession().refresh : null;
            // const refreshToken = localStorageService.getRefreshToken();
            return axios
                .post(apiUrl, {
                    refresh: refreshToken,
                })
                .then((res) => {
                    if (res.status === 200) {
                        let loginUserDetails = getStoredSession() ? getStoredSession() : null;
                        if (loginUserDetails) {
                            loginUserDetails.access = res.data.access;
                            setStoredSession(loginUserDetails);
                        }
                        const token = getStoredSession() ? getStoredSession().access : null;
                        // localStorageService.setToken(res.data);
                        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
                        return axios(originalRequest);
                    }
                })
                .catch(function (error) {
                    console.log(error);
                });
        } else if (status === 404 || status === 400) {
            // Unless the caller has already shown the reader what happened — see requestNotices.
            if (announcesItsFailures(originalRequest)) myFunction(error);
        } else if (
            originalRequest?.url?.includes("token/refresh/") &&
            status === 401 &&
            !originalRequest._retry
        ) {
            removeStoredSession();
        }
        return Promise.reject(error);
    }
);
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <>
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <GlobalStyle></GlobalStyle>
                <CircularIndeterminate/>
                <div id="snackbarTemp"></div>
                <ScrollToTop/>
                <App/>
            </ThemeProvider>
        </BrowserRouter>
    </>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();


export default function ScrollToTop() {
    const {pathname} = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}